import { useState, type ReactNode } from 'react'
import { Button, Skeleton } from '@cannect/new-components/atoms'
import { QuantityCounter } from '@cannect/new-components/molecules'
import { CycleSelector } from '@cannect/new-components/organisms/Recurrency/CycleSelector'
import { formatToBRL } from '@cannect/utils/formatters'
import { RECURRENCY_MVP_SETTINGS } from '@cannect/services/resources/recurrency/constants'
import { usePatchSubscription } from '@cannect/services/resources/recurrency/hooks'
import {
  formatApiDate,
  getBlockedReason,
  getRecurrencyError,
  type RecurrencyErrorInfo
} from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail, SubscriptionItem } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { activeItems, maxQuantityFor, useEditDraft } from '../hooks/useEditDraft'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'

const copy = subscriptionsCopy.items

type ItemsSectionProps = {
  detail: SubscriptionDetail
  onAddItem: () => void
  onRemoveItem: (item: SubscriptionItem) => void
  onRequestPrescription?: () => void
}

type FieldError = { code?: string; message: string; max?: number | null }

const ItemRow = ({ item, children }: { item: SubscriptionItem; children?: ReactNode }) => (
  <li className="flex flex-col gap-2 rounded-lg border border-solid border-neutral-100 p-3 sm:flex-row sm:items-center">
    <div className="flex min-w-0 flex-1 items-center gap-3">
      {item.image ? (
        <img src={item.image} alt={item.name || 'Produto'} className="h-14 w-14 shrink-0 rounded-md object-contain" />
      ) : (
        <span aria-hidden className="h-14 w-14 shrink-0 rounded-md bg-neutral-50" />
      )}
      <div className="flex min-w-0 flex-col text-sm">
        <span className="font-semibold text-neutral-800">{item.name || 'Produto'}</span>
        <span className="text-neutral-600">
          {copy.quantity(item.quantity)}
          {item.unitPrice !== null && (
            <>
              {' · '}
              {formatToBRL(item.unitPrice)} {copy.withDiscount}
            </>
          )}
        </span>
        {item.lineTotal !== null && (
          <span className="font-semibold text-neutral-800">{formatToBRL(item.lineTotal)}</span>
        )}
        {item.unavailable && (
          <span className="flex flex-col">
            <span className="self-start rounded bg-warning-200 px-2 py-0.5 text-xs font-semibold text-neutral-800">
              {copy.unavailable}
            </span>
            <span className="text-xs text-neutral-600">{copy.unavailableHelp}</span>
          </span>
        )}
      </div>
    </div>
    {children}
  </li>
)

export const ItemsSection = ({ detail, onAddItem, onRemoveItem, onRequestPrescription }: ItemsSectionProps) => {
  const [editing, setEditing] = useState(false)
  const [fieldError, setFieldError] = useState<FieldError | null>(null)
  const draft = useEditDraft(detail)
  const items = activeItems(detail)
  const open = detail.status === 'active' || detail.status === 'paused'
  const blocked = open ? getBlockedReason(detail, 'edit') : null

  const save = useSubscriptionAction({
    detail,
    mutation: usePatchSubscription(detail.id),
    event: 'recurrency_edit',
    eventPayload: () => ({
      change: draft.changedItems.length ? 'quantity' : 'cycle',
      cycleDays: draft.cycleDays,
      items: items.map((item) => ({
        productId: item.productId,
        quantity: draft.quantities[item.productId] ?? item.quantity
      }))
    }),
    fieldErrorCodes: ['QUANTITY_ABOVE_PRESCRIPTION', 'QUANTITY_OUT_OF_RANGE', 'INVALID_CYCLE'],
    onFieldError: (err: RecurrencyErrorInfo) =>
      setFieldError({ code: err.code, message: err.message, max: err.items?.[0]?.allowedQuantityPerCycle ?? null }),
    onSuccess: () => {
      setEditing(false)
      setFieldError(null)
    }
  })

  const previewError = draft.preview.error ? getRecurrencyError(draft.preview.error).message : null
  const lockedByDraft = editing && draft.dirty

  const discard = () => {
    draft.reset()
    setFieldError(null)
    setEditing(false)
  }

  return (
    <section
      aria-labelledby="sub-items"
      className="flex flex-col gap-4 rounded-xl border border-solid border-neutral-100 bg-white p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="sub-items" className="text-lg font-semibold text-neutral-800">
          {subscriptionsCopy.sections.items}
        </h2>
        {open && !editing && detail.canEdit && (
          <Button variant="outline_light" size="md" onClick={() => setEditing(true)}>
            {subscriptionsCopy.buttons.edit}
          </Button>
        )}
      </div>

      {/* Ativa com ciclo travado já tem a faixa no cabeçalho. */}
      {detail.status === 'paused' && blocked && <p className="m-0 text-sm text-neutral-600">{blocked}</p>}

      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((item) => {
          const max = maxQuantityFor(item)
          const quantity = draft.quantities[item.productId] ?? item.quantity
          return (
            <ItemRow key={item.productId} item={item}>
              <div className="flex flex-wrap items-center gap-2">
                {editing && (
                  <div className="flex flex-col items-start gap-1">
                    <span className="sr-only" id={`qty-${item.productId}`}>
                      Quantidade de {item.name}
                    </span>
                    <div role="group" aria-labelledby={`qty-${item.productId}`}>
                      <QuantityCounter
                        quantity={quantity}
                        onChangeQuantity={(next) => {
                          setFieldError(null)
                          draft.setQuantity(item.productId, Math.min(max, Math.max(1, next)))
                        }}
                      />
                    </div>
                    {quantity >= max && <span className="text-xs text-neutral-600">{copy.maxReached(max)}</span>}
                  </div>
                )}
                {open && detail.canEdit && (
                  <Button
                    variant="outline_light"
                    size="md"
                    disabled={lockedByDraft}
                    title={lockedByDraft ? copy.saveOrDiscardFirst : undefined}
                    onClick={() => onRemoveItem(item)}>
                    {subscriptionsCopy.buttons.remove}
                    <span className="sr-only"> {item.name}</span>
                  </Button>
                )}
              </div>
            </ItemRow>
          )
        })}
      </ul>

      {fieldError && fieldError.code !== 'INVALID_CYCLE' && (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-red-700">
          <span>
            {fieldError.code === 'QUANTITY_ABOVE_PRESCRIPTION' && fieldError.max
              ? copy.aboveAllowed(fieldError.max)
              : fieldError.message}
          </span>
          {fieldError.code === 'QUANTITY_ABOVE_PRESCRIPTION' && onRequestPrescription && (
            <Button unstyled className="font-semibold text-primary-700 underline" onClick={onRequestPrescription}>
              {copy.sendNewPrescription}
            </Button>
          )}
        </div>
      )}

      {open && detail.canEdit && (
        <div className="flex flex-col gap-1">
          <div>
            <Button variant="outline_light" size="md" disabled={lockedByDraft} onClick={onAddItem}>
              {subscriptionsCopy.buttons.addItem}
            </Button>
          </div>
          {lockedByDraft && <span className="text-xs text-neutral-600">{copy.saveOrDiscardFirst}</span>}
        </div>
      )}

      {editing && (
        <div className="flex flex-col gap-4 border-t border-solid border-neutral-100 pt-4">
          <CycleSelector
            options={RECURRENCY_MVP_SETTINGS.cycleDaysOptions}
            value={draft.cycleDays}
            label={copy.cycleLabel}
            onChange={(days) => {
              setFieldError(null)
              draft.setCycleDays(days)
            }}
          />
          {fieldError?.code === 'INVALID_CYCLE' && (
            <p role="alert" className="m-0 text-sm text-red-700">
              {fieldError.message}
            </p>
          )}

          {draft.dirty && (
            <div aria-live="polite" className="flex flex-col gap-1 rounded-lg bg-neutral-50 p-3 text-sm">
              <span className="font-semibold text-neutral-800">{copy.previewTitle}</span>
              {previewError && <span className="text-red-700">{previewError}</span>}
              {!previewError && draft.previewPending && !draft.preview.data && <Skeleton className="h-12 w-full" />}
              {!previewError && draft.preview.data && (
                <dl className="m-0 flex flex-col gap-1">
                  <div className="flex justify-between">
                    <dt>{subscriptionsCopy.header.shipping}</dt>
                    <dd>
                      {draft.preview.data.estimatedShipping !== null
                        ? formatToBRL(draft.preview.data.estimatedShipping)
                        : subscriptionsCopy.header.shippingUnknown}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{subscriptionsCopy.header.total}</dt>
                    <dd className="font-semibold">{formatToBRL(draft.preview.data.estimatedAmount ?? undefined)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>{subscriptionsCopy.header.nextCharge}</dt>
                    <dd>{formatApiDate(draft.preview.data.nextChargeAt)}</dd>
                  </div>
                </dl>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline_light" onClick={discard}>
              {subscriptionsCopy.buttons.discard}
            </Button>
            <Button disabled={!draft.dirty} isLoading={save.isPending} onClick={() => save.run(draft.patchBody)}>
              {subscriptionsCopy.buttons.save}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
